import type { LanguageInfo } from '../types';

/**
 * Language patterns for web-related languages
 */
export const webLanguagePatterns: LanguageInfo[] = [
  {
    name: 'typescript',
    patterns: [
      /interface\s+\w+/,
      /type\s+\w+\s*=/,
      /export\s+(type|interface|class)/,
      /:\s*(string|number|boolean|any|void|unknown)\b/,
      /<[T\w]+>/,
      /as\s+(const|[A-Z]\w+)/,
      /\w+\?:/
    ],
    confidencePatterns: [
      /:\s*(?:string|number|boolean|any|void|unknown)\b/, // Type annotations are strong TS indicators
      /interface\s+\w+/,
      /namespace\s+\w+/
    ],
    negativePatterns: [
      /^\s*<\?php/, // PHP opening tag
      /^#!\s*\/usr\/bin\/env\s+node/, // Node shebang
      /^#!\s*\/usr\/bin\/env\s+python/ // Python shebang
    ],
    requiresMatch: 1,
    extensions: ['.ts', '.tsx'],
    keywords: ['interface', 'type', 'namespace', 'enum', 'implements', 'declare', 'readonly']
  },
  {
    name: 'javascript',
    patterns: [
      /const\s+\w+\s*=/,
      /let\s+\w+\s*=/,
      /function\s+\w+\s*\(/,
      /\bclass\s+\w+/,
      /\b(?:import|export)\b.*(?:from\s+['"]|{|\*)/,
      /\basync\s+function/,
      /\bawait\s+/,
      /^\s*\{[\s\S]*(?:function|=>|const|let|var|return)[\s\S]*\}\s*$/,
      /^\s*\{[\s\S]*(?:\/\/|\/\*)[\s\S]*\}\s*$/
    ],
    confidencePatterns: [
      /\b(?:const|let)\s+\w+\s*=/,
      /\basync\s+function/,
      /^\s*\{[\s\S]*(?:function|=>|const|let|var|return|\/\/|\/\*)[\s\S]*\}\s*$/
    ],
    negativePatterns: [
      /:\s*(?:string|number|boolean)\b/, // TypeScript type annotations
      /^\s*<\?php/, // PHP opening tag
      /^#!\s*\/usr\/bin\/env\s+python/, // Python shebang
      /^[\s\n]*\{\s*"[\w-]+"\s*:/, // JSON object start
      /"[\w-]+"\s*:\s*(?:"|{|\[|\d+|true|false|null)/, // JSON key-value pairs
      /"(?:const|let|var|function|class|await|async)"\s*:/, // Keywords as JSON keys
      /:\s*"(?:const|let|var|function|class|await|async)"/, // Keywords as JSON string values
      /"[\w-]+"\s*:\s*function/, // 'function' as a JSON value
      /"[\w-]+"\s*:\s*class/, // 'class' as a JSON value
      /"[\w-]+"\s*:\s*(?:const|let|var)/ // Declaration keywords as JSON values
    ],
    requiresMatch: 2,
    extensions: ['.js', '.jsx'],
    keywords: ['const', 'let', 'var', 'function', 'class', 'await', 'async']
  },
  {
    name: 'html',
    patterns: [
      /<(!DOCTYPE|html|head|body|div|span|p|a)\b/i,
      /<\/[a-z]+>/i,
      /<(input|img|br|hr|link|meta)\b[^>]*>/i, // Self-closing tags
      /class=["'][^"']*["']/,
      /id=["'][^"']*["']/,
      /style=["'][^"']*["']/,
      /<script\b[^>]*>[\s\S]*?<\/script>/i,
      /<style\b[^>]*>[\s\S]*?<\/style>/i
    ],
    confidencePatterns: [
      /<!DOCTYPE\s+html>/i,
      /<html\b/i,
      /<div\b[^>]*class=/i,
      /<link\b[^>]*rel=["']stylesheet["']/i,
      /<meta\b[^>]*charset=/i
    ],
    negativePatterns: [
      /^<\?xml\s+version=/, // XML declaration
      /<\w+:\w+>/, // XML namespaces
      /xmlns:/, // XML namespace declarations
      /<soap:/, // SOAP XML
      /<wsdl:/ // WSDL XML
    ],
    requiresMatch: 1,
    extensions: ['.html', '.htm', '.xhtml'],
    keywords: [
      'html',
      'head',
      'body',
      'div',
      'span',
      'class',
      'id',
      'script',
      'style',
      'link',
      'meta',
      'title',
      'header',
      'footer',
      'nav',
      'main',
      'section',
      'article'
    ]
  },
  {
    name: 'css',
    patterns: [/[\.\#][\w-]+\s*\{/, /@media\s+/, /\b\w+:\s*[^;]+;/],
    extensions: ['.css'],
    keywords: ['margin', 'padding', 'color', 'background', '@media', '@import']
  },
  {
    name: 'urlLang',
    patterns: [
      /^\[?(?:https?|ftp|file):\/\/[-A-Za-z0-9+&@#/%?=~_|!:,.;]*[-A-Za-z0-9+&@#/%=~_|]\]?/,
      /^\[?(?:mailto:|tel:)[-A-Za-z0-9+&@#/%?=~_|!:,.;]*\]?/
    ],
    confidencePatterns: [/^https?:\/\//, /^ftp:\/\//, /^file:\/\//, /^mailto:/, /^tel:/],
    negativePatterns: [
      /^</, // HTML
      /^{/, // JSON/JavaScript
      /^#!/ // Shell scripts
    ],
    requiresMatch: 1,
    extensions: [],
    keywords: ['http', 'https', 'ftp', 'file', 'mailto', 'tel']
  }
];
